<script setup lang="ts">
const {t} = useI18n();

const colorMode = useColorMode();
// The default preference is 'system', so the resolved value must be checked:
// with preference === 'dark' the first click is a no-op for users whose
// system theme is already dark.
const isDark = computed(() => colorMode.value === 'dark');

// colorMode.value is only resolved on the client, so reading it during SSR
// would make the label and aria-pressed hydrate differently than they
// rendered. Hold the neutral state until mounted, then swap in the real one.
const mounted = ref(false);
onMounted(() => {
  mounted.value = true;
});

const label = computed(() =>
    mounted.value && isDark.value ? t('switchToLightMode') : t('switchToDarkMode')
);

function toggleTheme() {
  colorMode.preference = isDark.value ? 'light' : 'dark';
}
</script>

<template>
  <button type="button" class="theme-toggler"
          :aria-label="label"
          :aria-pressed="mounted && isDark"
          @click="toggleTheme">
    <span class="star star-1"></span>
    <span class="star star-2"></span>
    <span class="star star-3"></span>
    <svg class="peaks" viewBox="0 0 50 14" preserveAspectRatio="none" aria-hidden="true">
      <path d="M0 14 L11 4 L16 8 L26 0 L38 9 L43 5 L50 14 Z" />
    </svg>
    <span class="sky-body">
      <Icon :name="isDark ? 'ph:moon-stars-fill' : 'ph:sun-fill'" size="14px" />
    </span>
  </button>
</template>

<style lang="sass" scoped>
.theme-toggler
  position: relative
  display: flex
  align-items: center
  width: 50px
  height: 30px
  border: 1px solid rgba(0, 0, 0, 0.12)
  border-radius: 20px
  overflow: hidden
  background: linear-gradient(to bottom, #8ecbe8 0%, #d9edf5 100%)
  padding: 0
  cursor: pointer
  box-shadow: inset 0 0 5px rgba(20, 20, 20, 0.2)
  transition: background 400ms ease-in-out
  -webkit-tap-highlight-color: transparent

.dark-mode .theme-toggler
  border-color: rgba(255, 255, 255, 0.18)
  background: linear-gradient(to bottom, #0b1130 0%, #1c2650 100%)
  box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.6)

// The knob stays a solid, high-contrast puck in both themes (only the icon
// swaps colour) so it reads as a sliding switch handle at a glance, rather
// than blending into the sky-coloured track behind it.
.sky-body
  position: absolute
  top: 3px
  left: 3px
  width: 22px
  height: 22px
  border-radius: 50%
  display: flex
  align-items: center
  justify-content: center
  color: #f5a623
  background-color: #fffdf7
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.45)
  translate: 0
  transition: all 300ms ease-in-out
  z-index: 2

.dark-mode .sky-body
  translate: 22px
  color: #4a5aa8

.peaks
  position: absolute
  bottom: 0
  left: 0
  width: 100%
  height: 12px
  z-index: 1

  path
    fill: #3f6b52
    transition: fill 400ms ease-in-out

.dark-mode .peaks path
  fill: #2b3768

.star
  position: absolute
  width: 2px
  height: 2px
  border-radius: 50%
  background-color: white
  opacity: 0
  transition: opacity 300ms ease-in-out

.dark-mode .star
  opacity: 0.9

.star-1
  top: 4px
  left: 8px

.star-2
  top: 13px
  left: 15px

.star-3
  top: 7px
  left: 21px
</style>
